import {
  AnalyzeTinybirdResourceResponseBodyType,
  ConfigurationDto,
  ConversionStageReachedType,
  DatasetId,
  GraphConfig,
} from "@intelligems/ig-types";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { ISitewideDashboardFilters } from "@src/store/types/sitewideDashboardFilters";

/**
 * Stand-in for "apply no z-score limit". Sent to the API when the filter is
 * switched off, and high enough that nothing is ever excluded by it.
 */
export const NO_Z_SCORE_LIMIT = "9999";

/**
 * The z-score actually applied to a dashboard: the dashboard's own value wins
 * over the org default from Settings -> Analytics, and switching the filter off
 * means no limit at all.
 */
export function getMaxZScore(
  maxZScoreFilter: string | number | null | undefined,
  isZScoreEnabled: boolean,
  maxZScore: ConfigurationDto | undefined
): string | number {
  if (!isZScoreEnabled) {
    return NO_Z_SCORE_LIMIT;
  }

  return maxZScoreFilter ?? maxZScore?.value ?? NO_Z_SCORE_LIMIT;
}

export const getTinybirdActiveAnalyticsFilters = (
  filters: IExperienceDashboardFilters | ISitewideDashboardFilters,
  defaultMaxZScore: number
): Partial<IExperienceDashboardFilters> => {
  const result: Partial<IExperienceDashboardFilters> = {};

  if (filters.deviceType !== "any" && filters.deviceType !== null) {
    result.deviceType = filters.deviceType;
  }
  if ("customerType" in filters && filters.customerType !== null) {
    result.customerType = filters.customerType;
  }
  if (filters.visitorType !== "any" && filters.visitorType !== null) {
    result.visitorType = filters.visitorType;
  }
  if (filters.channels.length) {
    result.channels = filters.channels;
  }

  if (filters.countryCodes.length) {
    result.countryCodes = filters.countryCodes;
  }

  if (
    "analyticsViewType" in filters &&
    filters.analyticsViewType !== "sitewide_orders"
  ) {
    result.analyticsViewType = filters.analyticsViewType;
  }
  if ("customEvents" in filters && filters.customEvents.length) {
    result.customEvents = filters.customEvents;
  }
  if (
    "reachedCheckoutStage" in filters &&
    filters.reachedCheckoutStage !== "any" &&
    filters.reachedCheckoutStage !== null
  ) {
    result.reachedCheckoutStage = filters.reachedCheckoutStage;
  }

  if ("urlParam" in filters && filters.urlParam.queryParam !== null) {
    result.urlParam = filters.urlParam;
  }
  if (filters?.landingPage?.landingPageUrlPath != null) {
    result.landingPage = filters.landingPage;
  }
  if (filters.landingPageFilters?.length) {
    result.landingPageFilters = filters.landingPageFilters;
  }
  if (defaultMaxZScore !== filters.maxZScore) {
    result.maxZScore = filters.maxZScore;
  }

  if (filters.endTime) {
    result.endTime = filters.endTime;
  }
  if (filters.startTime) {
    result.startTime = filters.startTime;
  }

  if (
    "topN" in filters &&
    filters.topN !== null &&
    filters.pageName === "productGroups"
  ) {
    result.topN = filters.topN;
  }

  if ("excludeFreeOrders" in filters && filters.excludeFreeOrders === false) {
    result.excludeFreeOrders = filters.excludeFreeOrders;
  }

  return result;
};

export const transformMetricsConfig = (
  graphConfigs: GraphConfig[]
): { datasetId: DatasetId; metrics: string[] }[] => {
  const datasetMetricsMap = new Map<DatasetId, Set<string>>();

  const addMetricsToMap = (config: GraphConfig) => {
    const existing =
      datasetMetricsMap.get(config.datasetId) || new Set<DatasetId>();
    config.metrics.forEach((metric) => existing.add(metric));
    if (config.impactMetric) {
      existing.add(config.impactMetric);
    }
    datasetMetricsMap.set(config.datasetId, existing);
  };

  graphConfigs.forEach((config: GraphConfig) => {
    addMetricsToMap(config);
  });

  // Convert map to desired output format
  return Array.from(datasetMetricsMap.entries()).map(
    ([datasetId, metricsSet]) => ({
      datasetId,
      metrics: Array.from(metricsSet),
    })
  );
};

/**
 * `AnalyzeTinybirdResourceResponseBodySchema` types `metrics` as always
 * present, and node-backend fills it with `[]` before the response is parsed
 * against that schema — yet successful bodies still reach the dashboard
 * without it (Sentry 7638883854). Roughly twenty read sites take the field
 * straight off a type that promises an array, so the payload is made to match
 * its declared shape once, here at the boundary where an untrusted response
 * becomes a typed value, rather than at each reader.
 */
export const normalizeTinybirdAnalyticsResponse = (
  body: AnalyzeTinybirdResourceResponseBodyType
): AnalyzeTinybirdResourceResponseBodyType => ({
  ...body,
  metrics: body?.metrics ?? [],
});

/**
 * `?audience=` is user-controlled: it outlives the UI in bookmarks and shared
 * dashboard links, so values that are not members of the audience enums still
 * arrive today — one sampled event carries `?audience=channel`, and the label
 * this dashboard renders for `source_channel` is literally "Channel"
 * (`sitewideAnalytics/constants.ts`), which makes hand-edited and
 * hand-assembled links a plausible source. The Audience Dive route asserted
 * the raw param to its audience type and relied on `||`, which only rescues
 * `""`/`null`, so any other string reached `/v2/analytics/sitewide-analysis`
 * and was rejected by `z.enum(ExternalSitewideAnalyticsAudienceList)` on
 * `audience` (`packages/ig-types/src/schemas/analytics/analyze-sitewide.ts`)
 * with a 400 (Sentry 7569688803). The conversion funnel already validated
 * against an equivalent inline allowlist and now shares this helper. The param
 * is parsed once, here at the boundary where an untrusted string becomes a
 * typed value, rather than at each call site.
 *
 * `allowed` is the set of dimensions a given dashboard actually supports, and
 * is required because the dashboards no longer share one enum: the Audience
 * Dive page pivots on `ExternalSitewideAnalyticsAudienceList`, the conversion
 * funnel on the narrower `ConversionFunnelEntryPointList`. A value outside the
 * caller's set falls back instead of reaching the API.
 *
 * `allowed` is also the only argument `T` is inferred from. Without `NoInfer`
 * on `fallback`, `T` widens to the union of the two, so a fallback that is not
 * a member of `allowed` type-checks and this helper hands its caller exactly
 * the kind of value the endpoint's own `z.enum(...)` rejects with the 400
 * above — the failure it exists to prevent. `NoInfer` keeps the invariant
 * "fallback is a member of `allowed`" in the signature instead of leaving it to
 * each call site's return annotation; `utils.test.ts` pins it.
 */
export const parseAnalyticsAudienceParam = <T extends string>(
  value: string | null | undefined,
  fallback: NoInfer<T>,
  allowed: readonly T[]
): T => allowed.find((option) => option === value) ?? fallback;

export async function wrapAnalyticsRequest<T>(fetch: () => Promise<T>) {
  try {
    return await fetch();
  } catch (error) {
    if (import.meta.env.VITE_SUPPRESS_ANALYTICS_TOAST === "true") {
      error["suppressToast"] = true;
    }
    throw error;
  }
}

const BUILTIN_VIEWED_CONVERSION_STAGE_EVENTS =
  new Set<ConversionStageReachedType>([
    "checkout_block_viewed",
    "onsite_upsell_viewed",
    "post_purchase_offer_viewed",
  ]);

export const getConversionStageFilterPayload = ({
  customEvents,
  reachedCheckoutStage,
}: {
  customEvents: string[];
  reachedCheckoutStage: ConversionStageReachedType | null | undefined;
}) => ({
  triggeredCustomEvents:
    reachedCheckoutStage &&
    BUILTIN_VIEWED_CONVERSION_STAGE_EVENTS.has(reachedCheckoutStage)
      ? Array.from(new Set([...customEvents, reachedCheckoutStage]))
      : customEvents,
  reachedCheckoutStage:
    reachedCheckoutStage === null ||
    reachedCheckoutStage === "any" ||
    reachedCheckoutStage === "added_to_cart" ||
    (reachedCheckoutStage
      ? BUILTIN_VIEWED_CONVERSION_STAGE_EVENTS.has(reachedCheckoutStage)
      : false)
      ? null
      : reachedCheckoutStage || null,
});
